import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../../services/api';
import MediaLibraryModal from '../../../components/ui/MediaLibraryModal';

const BANNER_FIELDS = [
  {
    key: 'heroBannerImage',
    label: 'Hero Banner',
    helper: 'Shown on the right side of the Brands page hero section.'
  },
  {
    key: 'aboutBannerImage',
    label: 'About Section Banner',
    helper: 'Shown in the "About Our Brands" section near the bottom of the page.'
  }
];

const BrandsPageSettingsPage = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    heroBannerImage: '',
    aboutBannerImage: ''
  });
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
  const [activeField, setActiveField] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/settings/brands-page');
      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching brands page settings:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load brands page settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const openMediaLibrary = (key) => {
    setActiveField(key);
    setIsMediaLibraryOpen(true);
  };

  const handleImageSelect = (selectedMedia) => {
    if (activeField && Array.isArray(selectedMedia) && selectedMedia.length > 0) {
      handleChange(activeField, selectedMedia[0].url);
    }
    setIsMediaLibraryOpen(false);
    setActiveField(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await api.post('/admin/settings/brands-page', settings);
      if (response.data.success) {
        toast.success('Brands page settings saved successfully');
      }
    } catch (error) {
      console.error('Error saving brands page settings:', error);
      toast.error(error.response?.data?.message || 'Failed to save brands page settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin-ucla-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-slate-900 dark:text-admin-slate-100">Brands Page Banners</h1>
        <p className="mt-1 text-sm text-admin-slate-600 dark:text-admin-slate-400">
          Manage the banner images shown on the customer-facing Brands page. Leave a slot empty to use the default image.
        </p>
      </div>

      <div className="bg-white dark:bg-admin-slate-800 shadow-sm rounded-lg">
        <form onSubmit={handleSave} className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {BANNER_FIELDS.map(({ key, label, helper }) => (
              <div key={key} className="bg-admin-slate-50 dark:bg-admin-slate-700/40 border border-admin-slate-200 dark:border-admin-slate-600 rounded-lg p-4">
                <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-3">
                  {label}
                </label>

                <div className="flex flex-col gap-3">
                  <div className="w-full">
                    {settings[key] ? (
                      <div className="relative w-full aspect-square rounded-lg overflow-hidden border border-admin-slate-200 dark:border-admin-slate-700 bg-white">
                        <img
                          src={settings[key]}
                          alt={`${label} preview`}
                          className="w-full h-full object-contain"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 hover:opacity-100 transition-opacity flex items-end justify-center p-2">
                          <button
                            type="button"
                            onClick={() => openMediaLibrary(key)}
                            className="bg-admin-ucla-500 text-white text-xs px-2 py-1 rounded-md hover:bg-admin-ucla-600 transition-colors"
                          >
                            Change Image
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full aspect-square border-2 border-dashed border-admin-slate-300 dark:border-admin-slate-600 flex flex-col items-center justify-center rounded-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-admin-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <button
                          type="button"
                          onClick={() => openMediaLibrary(key)}
                          className="mt-2 px-3 py-1.5 bg-admin-ucla-500 text-white rounded-md hover:bg-admin-ucla-600 transition-colors text-sm"
                        >
                          Select Image
                        </button>
                      </div>
                    )}
                  </div>

                  {settings[key] && (
                    <button
                      type="button"
                      onClick={() => handleChange(key, '')}
                      className="text-red-600 hover:text-red-700 text-sm font-medium self-start"
                    >
                      Remove Image
                    </button>
                  )}

                  <p className="text-xs text-admin-slate-500 dark:text-admin-slate-400">{helper}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Save Button */}
          <div className="border-t border-admin-slate-200 dark:border-admin-slate-700 pt-6">
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="bg-admin-ucla-500 hover:bg-admin-ucla-600 text-white font-medium py-2 px-6 rounded-md transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </div>
                ) : (
                  'Save Brands Page Settings'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <MediaLibraryModal
        isOpen={isMediaLibraryOpen}
        onClose={() => {
          setIsMediaLibraryOpen(false);
          setActiveField(null);
        }}
        onSelect={handleImageSelect}
        maxSelect={1}
      />
    </div>
  );
};

export default BrandsPageSettingsPage;
